/**
 * Girar una nave en el plano de la finca: la ida y la vuelta, en un solo sitio.
 *
 * El giro NO se hornea en la geometría guardada. `geometry.perimeter`,
 * `blocks[].points` y las líneas los escribe el asistente de invernaderos y los
 * lee el kernel vendorizado; reescribirlos para colocar el plano sería usar el
 * dibujo de la nave como si fuera su posición. Se guarda un ÁNGULO aparte
 * (`geometry.healthMapRotationDeg`) y se aplica al pintar.
 *
 * LA REGLA QUE NO SE PUEDE ROMPER: lo que se dibuja y lo que se toca tienen que
 * ser la misma transformación. El render pone
 *
 *     transform="translate(ox, oy) rotate(deg, px, py)"
 *
 * y `toLocal` es su inversa exacta. Si las dos se separan, tocar un sector
 * devuelve el de al lado, que es peor que no poder girar. Por eso el giro va
 * con `rotatePoint` del kernel, cuya convención es la MISMA que la de SVG
 * (ángulo positivo = sentido horario en pantalla, porque la y crece hacia
 * abajo), y no con un seno y un coseno escritos aquí a mano.
 *
 * EL PIVOTE es el centro de la caja SIN GIRAR del perímetro, y es constante:
 * no depende del ángulo. Girar sobre el origen local movería la nave por la
 * pantalla además de girarla, y el desplazamiento guardado dejaría de
 * significar «dónde está». Sobre el centro, el ángulo y la posición son dos
 * cosas independientes y se pueden tocar por separado.
 */
import { rotatePoint } from "@/greenhouse-kernel/lib/layout/rotateGeometry";

export interface XY {
  x: number;
  y: number;
}

/** Lo mínimo que hace falta para situar una nave. Así se prueba sin modelo. */
export interface Placeable {
  offset: XY;
  /** Grados, horario en pantalla. 0 = como se dibujó. */
  rotationDeg: number;
  /** Centro de la caja sin girar, en coordenadas LOCALES de la nave. */
  pivot: XY;
}

/** Un ángulo cualquiera → [0, 360). `NaN`, infinitos y basura → 0. */
export function normalizeDeg(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number.NaN;
  if (!Number.isFinite(n)) return 0;
  return ((n % 360) + 360) % 360;
}

/**
 * El centro de la caja del perímetro. Con menos de un punto no hay caja, y se
 * devuelve el origen: una nave sin perímetro no se dibuja, pero que esto no
 * reviente evita un `NaN` viajando por todo el modelo.
 */
export function pivotOf(perimeter: readonly XY[]): XY {
  if (!perimeter.length) return { x: 0, y: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of perimeter) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

/** Local → mundo. La MISMA cuenta que hace el `transform` del grupo SVG. */
export function toWorld(gh: Placeable, local: XY): XY {
  const r = gh.rotationDeg ? rotatePoint(local, gh.rotationDeg, gh.pivot.x, gh.pivot.y) : local;
  return { x: r.x + gh.offset.x, y: r.y + gh.offset.y };
}

/**
 * Mundo → local. Inversa exacta de `toWorld`: primero se deshace el
 * desplazamiento y después el giro, en ese orden.
 *
 * El atajo de `rotationDeg === 0` no es una optimización cosmética: es el 100 %
 * de las naves de producción hoy, y las llamadas van dentro del bucle del
 * hit-test, que corre en cada toque y en cada movimiento del ratón.
 */
export function toLocal(gh: Placeable, world: XY): XY {
  const d = { x: world.x - gh.offset.x, y: world.y - gh.offset.y };
  if (!gh.rotationDeg) return d;
  return rotatePoint(d, -gh.rotationDeg, gh.pivot.x, gh.pivot.y);
}

/**
 * La huella de la nave YA GIRADA en el plano común.
 *
 * Sale de los vértices del perímetro girados, no de las cuatro esquinas de la
 * caja giradas: la caja de una caja girada es hasta un 41 % más grande a 45°, y
 * con eso el encuadre automático se alejaría de más y la barra de escala
 * mentiría. Con el perímetro real el resultado es ajustado.
 */
export function rotatedPlaneBox(
  gh: Placeable,
  perimeter: readonly XY[],
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of perimeter) {
    const w = toWorld(gh, p);
    if (w.x < minX) minX = w.x;
    if (w.y < minY) minY = w.y;
    if (w.x > maxX) maxX = w.x;
    if (w.y > maxY) maxY = w.y;
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Lo mismo pero en tamaño, que es lo que necesita el empaquetado automático. */
export function rotatedSize(gh: Placeable, perimeter: readonly XY[]): { w: number; h: number } {
  const b = rotatedPlaneBox({ ...gh, offset: { x: 0, y: 0 } }, perimeter);
  return { w: b.maxX - b.minX, h: b.maxY - b.minY };
}

/** Cada cuántos grados se pega el giro cuando se pide ayuda. */
export const SNAP_DEG = 15;

/**
 * Pega a múltiplos de 15°, pero solo si el dedo está CERCA (≤ 4°). Un imán que
 * agarra siempre impide dejar una nave a 37°, que es un ángulo tan legítimo
 * como cualquier otro: las naves reales no están alineadas al norte.
 */
export function snapDeg(deg: number, tolerance = 4): number {
  const d = normalizeDeg(deg);
  const nearest = Math.round(d / SNAP_DEG) * SNAP_DEG;
  return Math.abs(d - nearest) <= tolerance ? normalizeDeg(nearest) : d;
}

/** El ángulo del vector centro→dedo, en la misma convención que el resto. */
export function angleOf(center: XY, point: XY): number {
  return (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI;
}
